/* eslint-disable react/no-multi-comp */
// eslint-disable-next-line no-unused-vars
import * as React from "react";
import { memo } from "react";
import PropTypes from "prop-types";
import {
	MAX_PAYMENT_INSTALMENTS_WITH_FEES_DISPLAYED,
	PAYMENT_METHODS,
	PAYMENT_TERMS,
} from "app/constants";
import AmountContainer from "app/pages/.shared/AmountContainer";
import "./PaymentModesFieldset.scss";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import IconLogoAlma from "app/pages/.shared/static/icons/IconLogoAlma";
import { useFormikContext } from "formik";
import RadioFormik from "app/pages/.shared/form/RadioFormik";
import classNames from "classnames";
import IconLogoFloa from "app/pages/.shared/static/icons/IconLogoFloa";

const PaymentModesFieldset = ({
	total = 0,
	onChange,
	mensualite2Fois = {},
	paymentModes = [],
	isDepositEnabled,
	isBookingEligibleForDeposit = false,
	isPaymentAlmaAvailable,
	isPaymentFloaAvailable,
}) => {
	const intl = useIntl();
	const { values } = useFormikContext();

	const sortedPaymentModes = paymentModes
		.filter(paymentMode => {
			if (paymentMode.terms === PAYMENT_TERMS["2x"] && isDepositEnabled) {
				return isBookingEligibleForDeposit;
			}
			return true;
		})
		.sort((a, b) => {
			const getComparisonValue = paymentMode => {
				if (paymentMode.terms === PAYMENT_TERMS["1x"]) {
					return total;
				} else if (paymentMode.terms === PAYMENT_TERMS["2x"]) {
					return mensualite2Fois?.payNowAmount;
				}
				return paymentMode.instalments?.apport;
			};

			return getComparisonValue(b) - getComparisonValue(a);
		});

	return (
		<div className="payment-options__items">
			{sortedPaymentModes.map((paymentMode, index) => {
				const radioValue = paymentMode.terms;
				const isPaymentModeSelected =
					values?.payment?.mode.toString() === (radioValue && radioValue.toString());

				const paymentPartner = intl.formatMessage({
					id: `payment.method.${paymentMode.type}`,
				});

				let partnerLogo = null;

				if (paymentMode?.type === PAYMENT_METHODS.ALMA) {
					partnerLogo = <IconLogoAlma width={35} viewBox="0 10 35 15" />;
				} else if (paymentMode?.type === PAYMENT_METHODS.FLOA) {
					partnerLogo = <IconLogoFloa width={60} height={25} />;
				}

				const shouldDisplayNoFeesLabel =
					!paymentMode.instalments?.coutCredit &&
					paymentMode?.terms !== PAYMENT_TERMS["10x"];

				let headerComponent = (
					<div className="payment-options__line">
						<div>
							<div className="payment-options__line">
								<div
									className="payment-options__label payment-options__label--terms"
									data-testid="payment-options-label"
								>
									<FormattedMessage
										id="payment.methods.multiple.instlaments.label.simple"
										values={{
											terms: (
												<div className="payment-options__label-terms">
													<FormattedMessage
														id="payment.mode.terms.label"
														values={{
															terms: paymentMode.terms,
														}}
													/>
												</div>
											),
											sansFrais: shouldDisplayNoFeesLabel && (
												<FormattedMessage id="general.sans.frais" />
											),
											paymentType: (isPaymentAlmaAvailable ||
												isPaymentFloaAvailable) && (
												<div
													className={classNames({
														"payment-options__label-partner": true,
														"payment-options__label-partner-with-space": !shouldDisplayNoFeesLabel,
													})}
												>
													<FormattedMessage id="payment.methods.multiple.instlaments.paymentType.prefix" />
													{partnerLogo}
												</div>
											),
										}}
									/>
								</div>
							</div>
							<div className="payment-options__line">
								<div
									className="payment-options__sublabel payment-options__label--terms"
									data-testid="payment-options-sublabel"
								>
									{paymentMode.terms > PAYMENT_TERMS["4x"] ? (
										<FormattedMessage
											id="payment.methods.multiple.instlaments.label.partner"
											values={{
												terms: intl.formatMessage(
													{
														id: "general.payment.term.short",
													},
													{
														term: paymentMode.terms,
													}
												),
												paymentPartner,
											}}
										/>
									) : (
										<>
											<div className="payment-options__mensualites">
												(1 x{" "}
												<AmountContainer
													amount={paymentMode.instalments?.apport}
												/>
												) + ({paymentMode.terms - 1} x{" "}
												<AmountContainer
													amount={paymentMode.instalments?.mensualite}
												/>
												)
											</div>
											{paymentMode.instalments?.coutCredit > 0 && (
												<FormattedMessage
													id="payment.methods.multiple.instlaments.label.simple.2"
													values={{
														frais: (
															<AmountContainer
																amount={
																	paymentMode.instalments
																		?.coutCredit
																}
															/>
														),
													}}
												/>
											)}
											<FormattedMessage
												id="payment.methods.multiple.instlaments.sublabel"
												values={{
													paymentPartner,
												}}
												tagName="div"
											/>
											{isPaymentFloaAvailable && (
												<FormattedMessage id="payment.methods.multiple.instlaments.sublabel.floa" />
											)}
										</>
									)}
								</div>
							</div>
						</div>
						{paymentMode.terms <= MAX_PAYMENT_INSTALMENTS_WITH_FEES_DISPLAYED && (
							<div
								className="payment-options__pay-now-amount"
								data-testid="pay-now-amount"
							>
								<AmountContainer amount={paymentMode.instalments?.apport} />
							</div>
						)}
					</div>
				);

				if (paymentMode.terms === PAYMENT_TERMS["1x"]) {
					headerComponent = (
						<div className="payment-options__line">
							<div
								className="payment-options__label"
								data-testid="payment-options-label"
							>
								<FormattedMessage id="payment.methods.full.label.simple" />
							</div>
							<div
								className="payment-options__pay-now-amount"
								data-testid="pay-now-amount"
							>
								<AmountContainer amount={total} />
							</div>
						</div>
					);
				} else if (paymentMode.terms === PAYMENT_TERMS["2x"]) {
					headerComponent = (
						<div className="payment-options__line">
							<div>
								<div className="payment-options__line">
									<div
										className="payment-options__label payment-options__label--terms"
										data-testid="payment-options-label"
									>
										{isBookingEligibleForDeposit ? (
											<div className="payment-options__deposit">
												<FormattedMessage
													id="payment.methods.multiple.instlaments.label.deposit"
													values={{
														percentage: (
															<div className="payment-options__deposit-label">
																<FormattedMessage
																	id="payment.terms.description.payment.percentage"
																	values={{
																		percentage:
																			mensualite2Fois.percentagePayNow,
																	}}
																/>
															</div>
														),
													}}
												/>
											</div>
										) : (
											<FormattedMessage
												id="payment.methods.multiple.instlaments.label.simple"
												values={{
													terms: (
														<div className="payment-options__label-terms">
															<FormattedMessage
																id="payment.mode.terms.label"
																values={{
																	terms: 2,
																}}
															/>
														</div>
													),
													sansFrais: (
														<FormattedMessage id="general.sans.frais" />
													),
													paymentType: false,
												}}
											/>
										)}
									</div>
								</div>
								<div className="payment-options__line">
									<div
										className="payment-options__sublabel"
										data-testid="payment-options-sublabel"
									>
										<FormattedMessage
											id={
												isBookingEligibleForDeposit
													? "payment.methods.deposit.label.simple.1"
													: "payment.methods.partial.label.simple.1"
											}
											tagName="div"
											values={{
												percentage: (
													<FormattedMessage
														id="payment.terms.description.payment.percentage"
														values={{
															percentage:
																mensualite2Fois.percentagePayNow,
														}}
													/>
												),
												amountToPayNow: (
													<AmountContainer
														amount={mensualite2Fois.payNowAmount}
													/>
												),
											}}
										/>
										<FormattedMessage
											id={"payment.methods.partial.label.simple.2"}
											tagName="div"
											values={{
												solde: (
													<AmountContainer
														amount={mensualite2Fois.payLaterAmount}
													/>
												),
												date: (
													<span className="payment-options__date">
														<FormattedDate
															timeZone="UTC"
															day="2-digit"
															month="2-digit"
															year="numeric"
															value={mensualite2Fois.dueDate}
														/>
													</span>
												),
											}}
										/>
									</div>
								</div>
							</div>
							<div
								className="payment-options__pay-now-amount"
								data-testid="pay-now-amount"
							>
								<AmountContainer amount={mensualite2Fois.payNowAmount} />
							</div>
						</div>
					);
				}

				return (
					<div className="payment-options__item" key={index}>
						<RadioFormik
							showIcon={true}
							name="payment.mode"
							id={`payment-mode-${radioValue}`}
							value={radioValue}
							toggle={() => {
								onChange(radioValue);
							}}
							checked={{
								value: isPaymentModeSelected,
								shouldCheckTheValue: true,
							}}
							data-testid={`payment-mode-${radioValue}`}
						>
							<div className="payment-options__content">{headerComponent}</div>
						</RadioFormik>
					</div>
				);
			})}
		</div>
	);
};

PaymentModesFieldset.propTypes = {
	paymentModes: PropTypes.arrayOf(PropTypes.object),
	total: PropTypes.number,
	onChange: PropTypes.func,
	mensualite2Fois: PropTypes.shape({
		percentagePayNow: PropTypes.number,
		payLaterAmount: PropTypes.number,
		payNowAmount: PropTypes.number,
		dueDate: PropTypes.number,
	}),
	isDepositEnabled: PropTypes.bool,
	isBookingEligibleForDeposit: PropTypes.bool,
	isPaymentAlmaAvailable: PropTypes.bool,
	isPaymentFloaAvailable: PropTypes.bool,
};

export default memo(PaymentModesFieldset);
